import React, { useId } from 'react';
import { roundToTwoDecimals } from '../../utils/formatting';

const LABEL_CLASS = 'text-[11px] leading-tight text-slate-500 dark:text-gray-400';

export const PROPERTY_CONTROL_CLASS =
  'w-full min-w-0 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-1 text-[11px] text-slate-800 focus:border-primary-500 focus:bg-white focus:outline-none disabled:cursor-not-allowed disabled:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';

const CONTROL_SHELL_CLASS =
  'flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-1 focus-within:border-primary-500 focus-within:bg-white dark:border-gray-700 dark:bg-gray-800';

export const PropertyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mt-0.5 text-[10px] leading-snug text-slate-400 dark:text-gray-500">{children}</p>
);

export const PropertyTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span
    tabIndex={0}
    aria-label={text}
    className="group relative ml-1 inline-flex align-middle outline-none"
  >
    <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-slate-300 text-[9px] font-semibold text-slate-400 group-hover:border-primary-400 group-hover:text-primary-500 group-focus:border-primary-400 group-focus:text-primary-500 dark:border-gray-600 dark:text-gray-500">
      ?
    </span>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-0 z-50 mb-1.5 w-56 whitespace-pre-line rounded-md bg-slate-900 px-2 py-1.5 text-left text-[10px] font-normal leading-snug text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus:opacity-100 dark:bg-gray-700"
    >
      {text}
    </span>
  </span>
);

export const PropertyGroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="pt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-500">
    {children}
  </div>
);

type RowProps = {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  wide?: boolean;
  children: React.ReactNode;
};

export const PropertyRow: React.FC<RowProps> = ({ label, htmlFor, hint, wide, children }) => (
  <div>
    <div
      className={`grid items-center gap-2 ${
        wide ? 'grid-cols-[minmax(0,1fr)_8.75rem]' : 'grid-cols-[minmax(0,1fr)_6.75rem]'
      }`}
    >
      <label htmlFor={htmlFor} className={LABEL_CLASS}>
        {label}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
    {hint ? <PropertyHint>{hint}</PropertyHint> : null}
  </div>
);

export const PropertyInput: React.FC<{
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  hint?: React.ReactNode;
}> = ({ label, value, onChange, placeholder, required, hint }) => {
  const id = useId();
  return (
    <PropertyRow label={label} htmlFor={id} hint={hint} wide>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className={PROPERTY_CONTROL_CLASS}
      />
    </PropertyRow>
  );
};

export const PropertyNumber: React.FC<{
  label: React.ReactNode;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  unit?: string;
  hint?: React.ReactNode;
}> = ({ label, value, onChange, min, max, step, disabled, unit, hint }) => {
  const id = useId();
  return (
    <PropertyRow label={label} htmlFor={id} hint={hint}>
      <div className={`${CONTROL_SHELL_CLASS} ${disabled ? 'opacity-60' : ''}`}>
        <input
          id={id}
          type="number"
          value={roundToTwoDecimals(value)}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(e) => onChange(roundToTwoDecimals(parseFloat(e.target.value) || 0))}
          className="property-number w-full min-w-0 bg-transparent text-right text-[11px] tabular-nums text-slate-800 outline-none disabled:cursor-not-allowed disabled:text-slate-400 dark:text-gray-100"
        />
        {unit ? (
          <span className="shrink-0 text-[10px] text-slate-400 dark:text-gray-500">{unit}</span>
        ) : null}
      </div>
    </PropertyRow>
  );
};

export const PropertySelect: React.FC<{
  label: React.ReactNode;
  value: string;
  options: Array<string | { value: string; label: string }>;
  onChange?: (value: string) => void;
  disabled?: boolean;
  hint?: React.ReactNode;
}> = ({ label, value, options, onChange, disabled, hint }) => {
  const id = useId();
  return (
    <PropertyRow label={label} htmlFor={id} hint={hint} wide>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        className={PROPERTY_CONTROL_CLASS}
      >
        {options.map((option) => {
          const optValue = typeof option === 'string' ? option : option.value;
          const optLabel = typeof option === 'string' ? option : option.label;
          return (
            <option key={optValue} value={optValue}>
              {optLabel}
            </option>
          );
        })}
      </select>
    </PropertyRow>
  );
};

export const PropertyToggle: React.FC<{
  label: React.ReactNode;
  value: string;
  options: readonly [
    { value: string; label: string },
    { value: string; label: string },
  ];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => (
  <PropertyRow label={label} wide>
    <div
      role="radiogroup"
      aria-label={typeof label === 'string' ? label : undefined}
      className="grid grid-cols-2 rounded-md border border-slate-200 bg-slate-50 p-0.5 dark:border-gray-700 dark:bg-gray-800"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={`rounded px-1 py-0.5 text-[10px] font-medium capitalize transition-colors ${
              selected
                ? 'bg-white text-primary-600 shadow-sm dark:bg-gray-700 dark:text-primary-400'
                : 'text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </PropertyRow>
);

export const PropertyCheckbox: React.FC<{
  label: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: React.ReactNode;
}> = ({ label, checked, onChange, hint }) => {
  const id = useId();
  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr)_6.75rem] items-center gap-2">
        <label htmlFor={id} className={`${LABEL_CLASS} cursor-pointer`}>
          {label}
        </label>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-3.5 w-3.5 cursor-pointer justify-self-end rounded border-slate-300 text-primary-600 focus:ring-primary-500"
        />
      </div>
      {hint ? <PropertyHint>{hint}</PropertyHint> : null}
    </div>
  );
};

export const PropertyColor: React.FC<{
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => (
  <PropertyRow label={label}>
    <div className="flex items-center justify-end gap-1.5">
      <span className="font-mono text-[10px] text-slate-400 dark:text-gray-500">{value}</span>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={value}
        className="h-5 w-7 cursor-pointer rounded border border-slate-200 bg-transparent p-0 dark:border-gray-700"
      />
    </div>
  </PropertyRow>
);

export const ReadOnlyRow: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <PropertyRow label={label} wide>
    <div
      className="truncate text-right text-[11px] font-medium text-slate-700 dark:text-gray-200"
      title={typeof value === 'string' ? value : undefined}
    >
      {value}
    </div>
  </PropertyRow>
);

export const PropertyPanelHeader: React.FC<{
  title: string;
  subtitle?: string;
  color?: string;
  actions?: React.ReactNode;
}> = ({ title, subtitle, color, actions }) => (
  <div className="mb-2 flex items-center gap-2">
    {color ? (
      <span
        className="h-2 w-2 shrink-0 rounded-full ring-1 ring-black/10"
        style={{ background: color }}
      />
    ) : null}
    <div className="min-w-0 flex-1">
      <div
        className="truncate text-[13px] font-semibold leading-tight text-slate-800 dark:text-gray-100"
        title={title}
      >
        {title}
      </div>
      {subtitle ? (
        <div className="truncate text-[10px] text-slate-400 dark:text-gray-500">{subtitle}</div>
      ) : null}
    </div>
    {actions}
  </div>
);
